// PixiJS (WebGL) world renderer at a 360-pixel-high internal resolution (640 to 800 wide), nearest-neighbour scaled.
// Scenery is one cached texture; glow is pre-rendered sprites; particles are bounded pools.
import { Application, Container, Sprite, Graphics, Rectangle, Texture, TextureStyle, TilingSprite, UPDATE_PRIORITY, ColorMatrixFilter, AlphaFilter } from 'pixi.js';
/** the overlay's tint from the local night colour: its hue at full brightness, a third of the way from white */
const nightTint = (c: number) => {
  const r = (c >> 16) & 255, g = (c >> 8) & 255, b = c & 255, m = Math.max(r, g, b, 1), k = (v: number) => Math.round(255 - (255 - v * 255 / m) * 0.33);
  return (k(r) << 16) | (k(g) << 8) | k(b);
};
import { buildHdAd, AD_COLORS, HD_AD_W } from './hd-ads';
import { NEON } from './hd-kit';
import { CITY_ART, WORLD, CONFIG, VEHICLES, NPCS, HD_SCENES, WEAPONS, RARITIES, CHALLENGES, type HackId } from '../../common/src/data';
import { weaponSpec, type Item } from '../../common/src/rules';
import { PALETTE, COLOR, STATE } from './theme';
import { drawVent } from './vent-art';
import { crewOf, maskIndex } from './crew';
import { decoyFx, glitchBars } from './decoy-art';
import { GameMap, TILE, T, tileAt } from '../../common/src/map';
import { vSlip } from '../../common/src/sim';
import type { Snapshot, NpcSnap, PlayerSnap, VehicleSnap, BossSnap, DecoySnap, GameEvent } from '../../common/src/protocol';
import { WeaponFx, WEAPON_FX_REVIEW, comfortScale, muzzleFlash, WF } from './weapon-fx';
import { ShinganFx, decoyShimmer } from './shingan-fx';
import { ShinganArt, SHINGAN_ART } from './shingan-art';
import { facingSlot } from './sprite-sheet';
import { BUILD } from './net';
import { flashStrength } from './fx-pool';
import { HackFx, HACK_FX_REVIEW } from './hack-fx';
import { CountryFx } from './country-fx';
import type { HackTarget } from './targets';
import type { RefusalCode } from '../../common/src/hacks';
import { hash2 } from '../../common/src/util';
import * as art from './art';
import { BossArt } from './bossart';
import type { CityLight } from './blocks';
import { hdPieces, hdSignalHead, type HdLight, type HdPiece } from './hd-scene';
import { HdPainter, type HdPaintJob } from './hd-client';
import { hdDroneTex, hdIconTex } from './hd-icons';
import { rainIntensity } from './weather';
import { carTex, decoOf, shadowTex } from './hd-sprites';
import { carViewOn, carViewTex, TILT } from './car-view';
import { drawnCarTex, warmCars } from './car-drawn';
import './car-models';
import { CharacterRig } from './character-rig';
import type { MotionInput } from './character-motion';
import { cloudPuffs, PUFFS, PUFF_COLOR } from './smoke-art';
import { ClubFx, beamCanvas } from './hd-club-fx';
import { RAMP_BOOM } from './hd-place-carpark';
import { TWIN, TWIN_UPPER, paintTwinUpper, twinOcclusion } from './hd-twin-towers';
import { Lighting, LIGHT_PARAM_OFF } from './lighting';

// VH is fixed; VW widens from 16:9 up to 20:9 so wide phones fill the screen instead of letterboxing
export let VW = 640;
export const VH = 360, VW_MIN = 640, VW_MAX = 800;
/** Art/perspective prototype: render at 2x (same world view), upright people, 2x cars and the hd-scenes/*.json
 *  regions. On by default on this branch; `?hd=0` shows the current art for comparison. */
const Q = typeof location !== 'undefined' ? new URLSearchParams(location.search) : new URLSearchParams();
// Browser tests (navigator.webdriver) default to the 1x art: they test networking, prediction and gameplay, and painting
// the 2x scenes in CI's software GL made them run out of time. `?hd=1` forces the 2x art (tests/hd.e2e.ts, the dev
// screenshot scripts); players are never webdriver, so they always get it.
export const HD = Q.get('hd') === '1' || (Q.get('hd') !== '0' && !(typeof navigator !== 'undefined' && navigator.webdriver));
/** render resolution; `?res=1` keeps the HD art at 1x pixels (for comparing cost and look) */
const HD_RES = Q.get('res') === '1' || !HD ? 1 : 2;
const MAX_PARTICLES = 220, RAIN = HD ? 100 : 150; // thinner rain at 2x

const SLASH_SEC = 0.2, TANTO_SEC = 0.14, EMP_SEC = 0.5;
const hexNum = (c: string) => parseInt(c.slice(1), 16);
const inClub = (x: number, y: number) => { const z = WORLD.club.zone; return x >= z.x * TILE && y >= z.y * TILE && x < (z.x + z.w) * TILE && y < (z.y + z.h) * TILE; };

interface Particle { s: Sprite; vx: number; vy: number; life: number; max: number }
interface CharacterEntry { s: Sprite; ring: Sprite; rig?: CharacterRig; sh?: Sprite; /** waterline mask while standing in the hot spring */ wm?: Graphics; mz?: { bx: number; by: number; x: number; y: number } }
const reloadDurations = new Map<string, number>();
function reloadSeconds(w: string, rarity: string) {
  if (!reloadDurations.has(w)) reloadDurations.set(w, weaponSpec({ w, uid: '', r: 'common', roll: 1 } as Item)?.reload ?? 1.6);
  return reloadDurations.get(w)! * (RARITIES.tiers[rarity as keyof typeof RARITIES.tiers]?.reload ?? 1);
}

/** sideways speed (px/s) above which tyres leave marks */
const SKID_SLIP = 45;
const POLICE_KINDS = new Set(['cop', 'heavy', 'guard', 'drone', 'elite']);

export class Renderer {
  weaponFxOn = WEAPON_FX_REVIEW;
  /** hacking presentation (?hackfx=1): scan marks, lock-on, links, per-hack effects, persistent state, refusals; see hack-fx.ts */
  hackFxOn = HACK_FX_REVIEW;
  readonly hackFx = new HackFx();
  private hackGround = new Graphics();
  private intersections: { cx: number; cy: number }[] = [];
  readonly weaponFx = new WeaponFx();
  private weaponGround = new Graphics();
  private weaponPlayers: PlayerSnap[] = [];
  private weaponHitFilter?: ColorMatrixFilter;
  app = new Application();
  world = new Container(); ground = new Container(); decals = new Container(); infra = new Container(); pickups = new Container();
  vehicles = new Container(); chars = new Container(); fxl = new Container(); lights = new Container(); neon = new Container(); screen = new Container();
  proj = new Graphics(); smokeG = new Graphics(); cones = new Graphics(); marks = new Graphics();
  private slashes: { x: number; y: number; a: number; t: number; tanto: boolean; col: number }[] = [];
  private emps: { x: number; y: number; t: number }[] = [];
  decoyG = new Graphics(); boomG = new Graphics(); private boomLift = 0;
  private projOff = new Map<number, { ox: number; oy: number; x0: number; y0: number }>();
  pools = { veh: new Map<number, { s: Sprite; head: Sprite; siren: Sprite; key: string; sh?: Sprite }>(), chr: new Map<string, CharacterEntry>(), pk: new Map<string, Sprite>() };
  /** HD: rings and foot shadows (below everyone), and the upright scenery sorted with people by their feet */
  under = new Container(); uprights: { s: Sprite; half: number; tall: number }[] = [];
  signals: Sprite[][] = []; barrierG = new Graphics(); ventG = new Graphics(); private barKey = ''; fixG: Sprite[] = []; camS: Sprite[] = []; signGlows: { g: Sprite; base: number; ph: number }[] = []; adStrips: { t: TilingSprite; v: number; hs: Sprite }[] = [];
  puddles: { s: Sprite; base: number }[] = [];
  /** map texture pieces (art.mapChunks); with the glows and puddles they're hidden while off screen */
  chunks: Sprite[] = [];
  particles: Particle[] = []; rain: { s: Sprite; x: number; y: number; v: number }[] = [];
  scan!: Sprite; fog!: Sprite; private fogScan?: Texture; private fogOnly?: Texture;
  cam = { x: 0, y: 0, zoom: 1 };
  private lean = { x: 0, y: 0 };
  crt = true; t = 0; private motionTime = 0;
  /** skid marks: a fixed ring of small dark sprites on the decal layer, fading out (no per-frame allocation) */
  private skids: { s: Sprite; life: number }[] = []; private skidI = 0;
  /** camera shake left (s) and its strength (px), set by crashes and heavy hits near the camera */
  shakeT = 0; shakeAmp = 0;
  lowFx = false; // "Low effects" setting: no rain/fog/scanlines, fewer particles, no neon flicker
  userZoom = 1; // personal camera setting (1, 1.5, 2); multiplies the speed-based zoom
  /** dynamic lighting (lighting.ts); absent with `?light=0` */
  light?: Lighting; private lightOn = true; private neonLevel = 2;
  glow!: Texture; white!: Texture; private clubFx?: ClubFx;
  /** the sanctuary countryside's wildlife, petals and onsen steam (2x art only; country-fx.ts) */
  private country?: CountryFx;
  private shingan!: ShinganFx; private shArt?: ShinganArt; /** unlit overlays (above the lighting filter, same transform as the world) */ private unlit = new Container(); get shinganArtOn() { return !!this.shArt?.ready; } private shNow: { sg?: Snapshot['sg']; npcs: NpcSnap[] } = { npcs: [] };
  bossLayer = new Container(); private bossArt = new BossArt(this.bossLayer, HD ? { chars: this.chars, under: this.under } : undefined,
    // boss shakes step down with the comfort level (Full, Soft, Low) on top of the usual distance and Low-effects limits
    { shake: (a, x, y) => this.shake(a * ([1, 0.75, 0.5][this.neonLevel] ?? 0.5), x, y) });

  async init(parent: HTMLElement, map: GameMap) {
    TextureStyle.defaultOptions.scaleMode = 'nearest';
    await this.app.init({ width: VW, height: VH, antialias: false, resolution: HD_RES, background: '#07040f', preference: 'webgl', autoDensity: false });
    const cv = this.app.canvas as HTMLCanvasElement; cv.id = 'game'; parent.prepend(cv);
    // one throwing frame must not stop Pixi's ticker (it only requests the next frame after a clean one): reset the
    // filter stack the throw left half-pushed and keep drawing; the first error still reaches the error log
    // (the ticker holds the original app.render from init, so swap the listener rather than the method)
    let reported = false;
    this.app.ticker.remove(this.app.render, this.app);
    this.app.ticker.add(() => {
      try { this.app.render(); } catch (e) {
        const fs = this.dropStaleFilterTextures(); if (fs) fs._filterStackIndex = 0;
        if (!reported) { reported = true; setTimeout(() => { throw e; }); }
      }
    }, undefined, UPDATE_PRIORITY.LOW);
    this.glow = art.glowTex(64); this.white = art.pixelTex();
    if (HD) this.clubFx = new ClubFx(this.lights, this.glow, art.tex(beamCanvas())); // Kumadori Club beams, LED floor (hd-club-fx.ts)
    this.app.stage.addChild(this.world, this.unlit, this.screen);
    this.fxl.addChild(this.decoyG, this.boomG);
    this.shingan = new ShinganFx(this.under, this.fxl, this.chars);
    if (HD && SHINGAN_ART) {
      // own alpha filter: isolates the overlay so its 'erase' cut-outs only remove highlights, never the world below; the
      // filter's alpha is the comfort-graded strength (ShinganArt.endFrame)
      const iso = new AlphaFilter({ alpha: 1 }); iso.resolution = 'inherit'; this.unlit.filters = [iso];
      this.shArt = new ShinganArt('./art/shingan', BUILD, this.unlit);
    }
    if (HD) this.country = new CountryFx(this.under, this.fxl, map);
    this.world.addChild(this.ground, this.decals, this.neon, this.infra, this.pickups, this.vehicles, this.under, this.chars, this.bossLayer, this.smokeG, this.proj, this.fxl, this.lights, this.twinLayer, this.cones, this.marks);
    if (!LIGHT_PARAM_OFF) { this.light = new Lighting(this); this.light.setOn(this.lightOn); this.light.setLevel(this.neonLevel); } // after the world layers: its bloom sprites sit on top
    let lit: CityLight[] = [];
    for (const c of art.mapChunks(map, lit)) { const s = new Sprite(c.tex); s.x = c.x; s.y = c.y; this.ground.addChild(s); this.chunks.push(s); }
    // 2x scenes (common/data/hd-scenes) are painted piece by piece as the camera comes near (paintHdNear): the whole
    // map takes seconds to paint, which froze the page on load.
    const only = Q.get('scenes')?.split(','); // dev: `?scenes=id,id` paints only those scenes
    const scenes = HD ? HD_SCENES.filter(S => !only || only.includes(S.id)) : [];
    const inScene = (x: number, y: number) => scenes.some(({ rect: r }) => x >= r.x * TILE && x < (r.x + r.w) * TILE && y >= r.y * TILE && y < (r.y + r.h) * TILE);
    if (scenes.length) {
      // a 2x scene replaces the 1x city texture under it; its own lights replace the old ones there
      lit = lit.filter(l => !inScene(l.x + l.w / 2, l.y + l.h / 2));
      this.hdPainter = new HdPainter(map); this.hdQueue = scenes.flatMap(hdPieces);
    }
    if (HD) this.chars.sortableChildren = true;
    // Light sources in world px: the strong lights painted into the city's façades.
    const sources = lit.filter(l => l.alpha >= 0.4).map(l => ({ x: l.x + l.w / 2, y: l.y + l.h / 2, color: l.color }));
    // Pixel reflections stay near their actual light source and entirely on traversable ground.
    const pud = art.reflectionTex();
    const addReflection = (x: number, y: number) => {
      if (inScene(x, y)) return;
      let nearest = sources[0] ?? { x: 0, y: 0, color: '#536b82' }, distance = Infinity;
      for (const sg of sources) {
        const d = (sg.x - x) ** 2 + (sg.y - y) ** 2;
        if (d < distance) { distance = d; nearest = sg; }
      }
      const near = distance < CITY_ART.effects.reflectionReach ** 2;
      // A reflection sprite must not cross onto a roof, stall, cover tile or the map edge.
      for (let ty = Math.floor(y / TILE); ty <= Math.floor((y + 10 - 1) / TILE); ty++) {
        for (let tx = Math.floor(x / TILE); tx <= Math.floor((x + 26 - 1) / TILE); tx++) {
          const t = tileAt(map, tx, ty);
          if (![T.ROAD, T.SIDEWALK, T.ALLEY, T.PLAZA].includes(t)) return;
        }
      }
      const s = new Sprite(pud); s.x = x; s.y = y;
      s.tint = near ? parseInt(nearest.color.slice(1), 16) : 0x536b82;
      const base = near ? CITY_ART.effects.reflection : 0.055;
      s.alpha = base; this.decals.addChild(s); this.puddles.push({ s, base });
    };
    // 90 scattered puddles per district (Kabukicho's keep their original positions)
    WORLD.districts.forEach((d, k) => {
      for (let i = 0; i < 90; i++) {
        const tx = d.x + Math.floor(hash2(i + k * 1000, 7) * d.w), ty = d.y + Math.floor(hash2(3, i + k * 1000) * d.h);
        addReflection(tx * TILE + 2, ty * TILE + 4);
      }
    });
    // Façade lights: soft additive glow (below people and cars), a reflection under the strong ones.
    lit.forEach((l, i) => {
      const g = new Sprite(this.glow); g.anchor.set(0.5); g.x = l.x + l.w / 2; g.y = l.y + l.h / 2;
      g.width = l.w + 20; g.height = l.h + 16; g.tint = parseInt(l.color.slice(1), 16); g.blendMode = 'add';
      g.alpha = l.alpha * CITY_ART.effects.blockGlow; this.neon.addChild(g);
      this.signGlows.push({ g, base: g.alpha, ph: i * 2.3 });
      if (l.alpha >= 0.45 && l.h >= 12) addReflection(l.x + l.w / 2 - 13, l.y + l.h + 26);
    });
    WORLD.ads.forEach((ad, i) => {
      const h = WORLD.roads.h[i % 4]; const x0 = 200 + (i * 331) % 1500, y0 = (h - 3) * TILE + 3;
      if (!(inScene(x0, y0) || inScene(x0 + 180, y0))) {
        const s = new Sprite(art.adTex(ad)); s.x = x0; s.y = y0; s.alpha = 0.75;
        this.neon.addChild(s); this.signGlows.push({ g: s, base: 0.7, ph: i });
        return;
      }
      // 2x: a pole-mounted LED ticker on the sidewalk, scrolling a TilingSprite; it fades when someone stands behind it
      // (the `uprights` rule) and keeps 4.5 tiles clear of every shop counter / kiosk reach square
      let x = x0; const y = (h - 3) * TILE + 13, w = HD_AD_W / 2;
      for (let n = 0; n < 40 && WORLD.pois.some(p => { const qx = p.x * TILE + 8, qy = p.y * TILE + 8; return Math.hypot(Math.max(x - qx, 0, qx - (x + w)), qy - y) < 4.5 * TILE; }); n++) x += TILE;
      const A = buildHdAd(ad, AD_COLORS[i % 3]);
      const hs = new Sprite(art.tex(A.housing)); hs.scale.set(0.5); hs.anchor.set(0.5, 1); hs.x = x + w / 2; hs.y = y; hs.zIndex = y;
      const ts = new TilingSprite({ texture: art.tex(A.strip), width: A.win.w, height: A.win.h });
      ts.x = A.win.x - HD_AD_W / 2; ts.y = A.win.y - A.ay; hs.addChild(ts);
      this.chars.addChild(hs); this.uprights.push({ s: hs, half: w / 2, tall: A.ay / 2 });
      this.adStrips.push({ t: ts, v: 36 + (i % 3) * 8, hs });
      const gl = new Sprite(this.glow); gl.anchor.set(0.5); gl.x = x + w / 2; gl.y = y - (A.ay - 12) / 2; gl.width = w + 30; gl.height = 34;
      gl.tint = parseInt(NEON[AD_COLORS[i % 3]].slice(1), 16); gl.blendMode = 'add'; gl.alpha = 0.22;
      this.neon.addChild(gl); this.signGlows.push({ g: gl, base: 0.22, ph: i });
    });
    // infrastructure
    this.intersections = map.intersections;
    for (const it of map.intersections) {
      const lights: Sprite[] = [];
      // in the 2x scene the lamps sit in the signal heads on their poles (hd-scene.ts signalPost)
      const pole = inScene(it.cx, it.cy) ? 4 : 0, lift = pole ? hdSignalHead() : 0;
      for (const [dx, dy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        const s = new Sprite(this.glow); s.anchor.set(0.5); s.width = s.height = 10; s.blendMode = 'add';
        s.x = it.cx + dx * ((it.x1 - it.x0) / 2 + pole); s.y = it.cy + dy * ((it.y1 - it.y0) / 2 + pole) - lift; (pole ? this.lights : this.infra).addChild(s); lights.push(s);
      }
      this.signals.push(lights);
    }
    this.infra.addChild(this.barrierG, this.ventG);
    for (const f of WORLD.fixtures) { const s = new Sprite(HD ? hdIconTex('fix') : art.iconTex('fix')); s.anchor.set(0.5); if (HD) s.scale.set(0.5); s.x = f.x * TILE; s.y = f.y * TILE; this.infra.addChild(s); this.fixG.push(s); }
    for (const c of WORLD.cameras) { const s = new Sprite(HD ? hdIconTex('cam') : art.iconTex('cam')); s.anchor.set(0.3, 0.5); if (HD) s.scale.set(0.5); s.x = c.x * TILE; s.y = c.y * TILE; this.infra.addChild(s); this.camS.push(s); }
    for (const p of WORLD.pois.filter(p => p.kind === 'kiosk' || (p.kind === 'broker' && !HD))) { const s = new Sprite(HD ? hdIconTex('kiosk') : art.iconTex('kiosk')); s.anchor.set(0.5); if (HD) s.scale.set(0.5); s.x = p.x * TILE; s.y = p.y * TILE; this.infra.addChild(s); }
    // rain + fog + scanlines in screen space
    for (let i = 0; i < RAIN; i++) {
      const s = new Sprite(this.white); s.width = 1; s.height = 5 + Math.random() * 4; s.tint = 0x8fb8ff; s.alpha = 0.14; s.rotation = 0.25;
      this.screen.addChild(s); this.rain.push({ s, x: Math.random() * VW, y: Math.random() * VH, v: 300 + Math.random() * 200 });
    }
    this.fog = new Sprite(art.fogTex()); this.fog.width = VW; this.fog.height = VH; this.screen.addChild(this.fog);
    this.scan = new Sprite(art.scanlineTex()); this.scan.width = VW; this.scan.height = VH; this.screen.addChild(this.scan);
    if (HD) { // at 2x every full-screen layer costs 4x: fog and scanlines become one pass (two textures for the CRT toggle)
      const fog = art.fogTex(), [c, g] = art.canvas(320, VH);
      g.drawImage(fog.source.resource as CanvasImageSource, 0, 0, 320, VH);
      this.fogOnly = art.tex(c);
      g.drawImage(this.scan.texture.source.resource as CanvasImageSource, 0, 0, 320, VH);
      this.fogScan = art.tex(c);
      this.scan.visible = false;
    }
  }

  /** 2x scene pieces not painted yet, and the scenes whose props and live lights are already placed */
  private hdQueue: HdPiece[] = []; private hdPlaced = new Set<string>(); private hdPainter?: HdPainter;
  private hdPropTex = new Map<HTMLCanvasElement | ImageBitmap, Texture>(); // lamps, posts and bollards share their canvases

  private hdJob?: { piece: HdPiece; job: HdPaintJob };
  /** Paint 2x pieces a slice at a time (hd-scene.ts startHdPiece), one piece at once: a piece in view gets up to 24 ms per
   *  frame, one within 1.5 screens of it 6 ms, so nothing blocks a frame for long and the piece is ready before the camera
   *  arrives. The first frame that paints sets window.__hdPaintMs; window.__hdPaintStats has per-frame paint times. */
  private paintHdNear(x0: number, x1: number, y0: number, y1: number, margin: number) {
    if (!this.hdQueue.length && !this.hdJob) return;
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
    const near = (p: HdPiece, m: number) => p.x + p.ww > x0 - m && p.x < x1 + m && p.y + p.wh > y0 - m && p.y < y1 + m;
    const dist = (p: HdPiece) => (p.x + p.ww / 2 - cx) ** 2 + (p.y + p.wh / 2 - cy) ** 2;
    const inView = (p: HdPiece) => near(p, 0);
    if (!this.hdJob) {
      let best: HdPiece | undefined;
      for (const p of this.hdQueue) if (near(p, margin * 1.5) && (!best || +inView(p) - +inView(best) > 0 || inView(p) === inView(best) && dist(p) < dist(best))) best = p;
      if (!best) return;
      this.hdQueue = this.hdQueue.filter(p => p !== best);
      this.hdJob = { piece: best, job: this.hdPainter!.start(best, !this.hdPlaced.has(best.scene.id)) };
    }
    const t0 = performance.now(), { piece, job } = this.hdJob;
    const done = job.run(inView(piece) ? 24 : 6);
    if (done) { this.paintHd(piece, job); this.hdJob = undefined; }
    const dt = performance.now() - t0;
    const g = globalThis as { __hdPaintMs?: number; __hdPaintStats?: { maxMs: number; totalMs: number; frames: number; pieces: number } };
    g.__hdPaintMs ??= Math.round(dt);
    const s = g.__hdPaintStats ??= { maxMs: 0, totalMs: 0, frames: 0, pieces: 0 };
    s.maxMs = Math.max(s.maxMs, dt); s.totalMs += dt; s.frames++; if (!this.hdJob) s.pieces++;
  }
  private paintHd(p: HdPiece, job: HdPaintJob) {
    const c = job.canvas, a = job.art;
    const t = art.tex(c);
    t.source.autoGarbageCollect = false; // Pixi would re-upload an idle texture from the canvas we are about to release
    this.app.renderer.texture.initSource(t.source); // upload now, so the canvas can go
    const s = new Sprite(t); s.scale.set(0.5); s.x = p.x; s.y = p.y; this.ground.addChild(s); this.chunks.push(s);
    if ('close' in c) c.close(); else c.width = c.height = 0; // release the pixels (the texture is already uploaded)
    if (!a || this.hdPlaced.has(a.id)) return;
    this.hdPlaced.add(a.id);
    if (a.id === 'nishi-west') this.placeTwinTowers();
    for (const q of a.props) {
      let pt = this.hdPropTex.get(q.canvas); if (!pt) this.hdPropTex.set(q.canvas, pt = art.tex(q.canvas));
      const u = new Sprite(pt); u.scale.set(0.5); u.anchor.set(q.ax / q.canvas.width, q.ay / q.canvas.height); u.x = q.x; u.y = q.y; u.zIndex = q.y;
      this.chars.addChild(u); this.uprights.push({ s: u, half: q.canvas.width / 4, tall: q.ay / 2 });
    }
    this.sceneLights(a.lights); this.light?.addScene(a.all);
  }

  /** The Kanemori Twin Towers' upper floors (hd-twin-towers.ts) hang over the street north of the towers. Everything
   *  under them there is behind the towers, so they get their own layer above people, cars and street lights (lamp
   *  and headlight glow never shows through); nothing in front of the towers reaches that high on screen. A tower
   *  fades while a crew member (on foot or driving) is behind it (frame()). */
  private twinLayer = new Container();
  private twin: { s: Sprite; r: [number, number, number, number] }[] = [];
  private placeTwinTowers() {
    const [c, g] = art.canvas(TWIN_UPPER.w, TWIN_UPPER.h);
    paintTwinUpper(g);
    const source = art.tex(c).source;
    // one sprite per tower and one for the bridge, so only the part you stand behind turns see-through
    for (const r of twinOcclusion(TILE)) {
      const fx = (r[0] - TWIN.westX * TILE) * 2, fw = (r[2] - r[0]) * 2;
      const s = new Sprite(new Texture({ source, frame: new Rectangle(fx, 0, fw, TWIN_UPPER.h) }));
      s.scale.set(0.5); s.anchor.set(0, 1); s.x = r[0]; s.y = TWIN.top * TILE;
      this.twinLayer.addChild(s); this.twin.push({ s, r });
    }
  }

  /** HD scene: only the neon signs stay live (flickering glow sprites); every other light and all reflections are
   *  baked into the scene image (hd-scene.ts bakeLights). */
  private sceneLights(ls: HdLight[]) {
    ls.forEach((l, i) => {
      const g = new Sprite(this.glow); g.anchor.set(0.5); g.x = l.x + l.w / 2; g.y = l.y + l.h / 2; g.blendMode = 'add'; g.tint = parseInt(l.color.slice(1), 16);
      g.width = l.w + 44; g.height = l.h + 34; g.alpha = Math.min(1, l.alpha * 1.4); this.neon.addChild(g);
      this.signGlows.push({ g, base: g.alpha, ph: i * 2.3 });
    });
  }

  /** Settings > "Neon lighting" (ignored when the page was opened with `?light=0`) */
  setLighting(on: boolean) { this.lightOn = on; this.light?.setOn(on); }
  /** Settings > "Neon intensity": 0 Full, 1 Soft, 2 Low */
  setNeon(i: number) { this.neonLevel = i; this.light?.setLevel(i); }

  /** match the view's aspect to the screen (width clamped to VW_MIN..VW_MAX); returns true when it changed */
  fitAspect(screenW: number, screenH: number) {
    const w = Math.max(VW_MIN, Math.min(VW_MAX, Math.round(VH * screenW / screenH / 2) * 2));
    if (w === VW) return false;
    VW = w; this.app.renderer.resize(VW, VH); this.fog.width = VW; this.scan.width = VW;
    this.dropStaleFilterTextures();
    return true;
  }
  /** Pixi 8.21: a screen resize destroys the idle screen-sized pool textures, but the filter stack still holds last
   *  frame's input textures, and the next filter push reads their (now null) source and throws. That error stopped
   *  Pixi's ticker for good: a frozen world while movement and the HUD carried on (bug report 2026-10-12). */
  private dropStaleFilterTextures() {
    const fs = (this.app.renderer as unknown as { filter?: { _filterStack?: ({ inputTexture: unknown } | undefined)[]; _filterStackIndex?: number } }).filter;
    for (const f of fs?._filterStack ?? []) if (f) f.inputTexture = null;
    return fs;
  }

  worldToView(x: number, y: number): [number, number] { return [(x - this.cam.x) * this.cam.zoom + VW / 2, (y - this.cam.y) * this.cam.zoom + VH / 2]; }
  viewToWorld(x: number, y: number): [number, number] { return [(x - VW / 2) / this.cam.zoom + this.cam.x, (y - VH / 2) / this.cam.zoom + this.cam.y]; }

  burst(x: number, y: number, n: number, col: number, speed: number, life = 0.5, size = 1) {
    if (this.lowFx) n = Math.ceil(n * 0.35);
    for (let i = 0; i < n; i++) {
      let p = this.particles.length < MAX_PARTICLES ? null : this.particles.shift()!;
      if (!p) { const s = new Sprite(this.white); this.fxl.addChild(s); p = { s, vx: 0, vy: 0, life: 0, max: 0 }; }
      const a = Math.random() * 6.28, v = speed * (0.3 + Math.random());
      p.s.x = x; p.s.y = y; p.s.width = p.s.height = size; p.s.tint = col; p.s.alpha = 1; p.s.visible = true;
      p.vx = Math.cos(a) * v; p.vy = Math.sin(a) * v; p.life = p.max = life * (0.6 + Math.random() * 0.6);
      this.particles.push(p);
    }
  }

  /** the car park's ramp barrier: the boom lifts while any vehicle is at the foot of the ramp and lowers otherwise (visual only) */
  private updateBoom(b: NonNullable<typeof RAMP_BOOM>, dt: number, vehicles: VehicleSnap[]) {
    const g = this.boomG, near = vehicles.some(v => Math.hypot(v.x - b.trigX, v.y - b.trigY) < b.trigR);
    this.boomLift += Math.max(-dt * 2.5, Math.min(dt * 2.5, (near ? 1 : 0) - this.boomLift));
    g.clear();
    if (Math.abs(b.px - this.cam.x) > VW || Math.abs(b.py - this.cam.y) > VH) return; // off screen: nothing to draw
    const a = this.boomLift * 0.85, dx = -Math.cos(a), dy = -Math.sin(a), n = 9, step = b.len / n;
    for (let i = 0; i < n; i++) { const x = b.px + dx * i * step, y = b.py + dy * i * step; g.rect(x - 1.75, y - 1.25, 3.5, 2.5).fill(b.cols[i & 1]); }
  }

  frame(dt: number, o: {
    s: Snapshot; players: PlayerSnap[]; vehicles: VehicleSnap[]; npcs: NpcSnap[]; you: number;
    focus: { x: number; y: number; speed: number; lx?: number; ly?: number }; scanner: boolean; hackMode: boolean; highlight: string | null; targets: HackTarget[];
    flash?: Map<number, number>; wanted?: boolean; bosses?: BossSnap[]; decoys?: DecoySnap[];
  }) {
    this.t += dt;
    this.motionTime = performance.now() / 1000; // character actions keep real timing when an effects frame is capped
    this.weaponPlayers = o.players;
    if (!this.weaponGround.parent) this.decals.addChild(this.weaponGround);
    this.weaponGround.visible = this.weaponFxOn;
    if (!this.hackGround.parent) this.infra.addChild(this.hackGround); // not the decals layer: its alpha is the wet-street reflection strength
    this.hackGround.clear();
    // camera: straight down; zoom out smoothly with vehicle speed
    const zTarget = (1 - Math.min(1, Math.max(0, (o.focus.speed - 60) / 200)) * 0.38) * this.userZoom;
    this.cam.zoom += (zTarget - this.cam.zoom) * Math.min(1, dt * 2.5);
    // optic look-ahead: lean toward the aim, eased so it never snaps
    const k = Math.min(1, dt * 6); this.lean.x += ((o.focus.lx ?? 0) - this.lean.x) * k; this.lean.y += ((o.focus.ly ?? 0) - this.lean.y) * k;
    this.cam.x = o.focus.x + this.lean.x; this.cam.y = o.focus.y + this.lean.y;
    const z = this.cam.zoom;
    this.world.scale.set(z);
    let jx = 0, jy = 0;
    if (this.shakeT > 0) { this.shakeT -= dt; const k = this.shakeAmp * Math.max(0, this.shakeT) / 0.3; jx = (Math.random() - 0.5) * 2 * k; jy = (Math.random() - 0.5) * 2 * k; }
    this.world.x = Math.round((VW / 2 - this.cam.x * z + jx) * HD_RES) / HD_RES; this.world.y = Math.round((VH / 2 - this.cam.y * z + jy) * HD_RES) / HD_RES;
    this.fog.visible = !this.lowFx;
    if (this.fogScan) this.fog.texture = this.crt ? this.fogScan : this.fogOnly!;
    else this.scan.visible = this.crt && !this.lowFx;
    const s = o.s;
    // only what's on screen is drawn or animated: the city is 4096x2304 px, the view about 640x360
    const vx0 = this.cam.x - VW / 2 / z - 64, vx1 = this.cam.x + VW / 2 / z + 64, vy0 = this.cam.y - VH / 2 / z - 64, vy1 = this.cam.y + VH / 2 / z + 64;
    const onScreen = (sp: Sprite) => {
      const x = sp.x - sp.anchor.x * sp.width, y = sp.y - sp.anchor.y * sp.height; // world px (these sprites aren't rotated)
      return x + sp.width > vx0 && x < vx1 && y + sp.height > vy0 && y < vy1;
    };
    this.paintHdNear(vx0, vx1, vy0, vy1, VW / z);
    this.clubFx?.update(this.t, this.cam, VW, VH, this.lowFx);
    if (HD && RAMP_BOOM) this.updateBoom(RAMP_BOOM, dt, o.vehicles);
    for (const c of this.chunks) c.visible = onScreen(c);
    // neon flicker (restrained)
    for (const g of this.signGlows) {
      g.g.visible = onScreen(g.g); if (!g.g.visible) continue;
      g.g.alpha = this.lowFx ? g.base : g.base * (0.85 + 0.15 * Math.sin(this.t * 3 + g.ph)) * (hash2(Math.floor(this.t * 8), g.ph * 10) > 0.985 ? 0.3 : 1);
    }
    for (const a of this.adStrips) if (a.hs.visible) a.t.tilePosition.x = -Math.round(this.t * a.v * 2) % a.t.texture.width; // 2x ticker text: hd px per second
    for (const p of this.puddles) { p.s.visible = onScreen(p.s); if (p.s.visible) p.s.alpha = p.base * (this.lowFx ? 0.8 : 0.8 + 0.2 * Math.sin(this.t * 5 + p.s.x)); }
    // signals
    s.inf.sig.forEach((ph, i) => {
      const L = this.signals[i]; if (!L) return;
      L.forEach((sp, k) => { const horiz = k % 2 === 0; const green = ph === 2 ? false : horiz ? ph === 1 : ph === 0; sp.tint = ph === 2 ? 0xff2040 : green ? 0x40ff90 : 0xff3040; sp.alpha = ph === 2 ? 0.6 + 0.4 * Math.sin(this.t * 10) : 0.9; });
    });
    // barriers
    const barKey = s.inf.bar.join(''); // barriers only change when hacked or on their timers: don't rebuild the geometry every frame
    if (barKey !== this.barKey) {
      this.barKey = barKey;
      const bg = this.barrierG; bg.clear();
      WORLD.barriers.forEach((b, i) => {
        const x = b.x * TILE, y = b.y * TILE, w = b.w * TILE, h = b.h * TILE;
        if (s.inf.bar[i]) {
          bg.rect(x, y + (h > w ? 0 : 5), w, h > w ? h : 6).fill(0xffe03a);
          for (let k = 0; k < Math.max(w, h); k += 8) bg.rect(h > w ? x : x + k, h > w ? y + k : y + 5, h > w ? w : 4, h > w ? 4 : 6).fill(0x202028);
        } else bg.rect(x, y + (h > w ? 0 : 7), w, h > w ? h : 2).fill({ color: 0xffe03a, alpha: 0.25 });
      });
    }
    // cameras + cones
    const cn = this.cones; cn.clear();
    WORLD.cameras.forEach((c, i) => {
      const [a100, jam] = s.inf.cam[i] ?? [0, 0]; const a = a100 / 100;
      const sp = this.camS[i]; sp.rotation = a; sp.tint = jam ? 0x606060 : 0xffffff;
      if (!jam) {
        const R = CONFIG.camera.range, f = CONFIG.camera.fov / 2, x = c.x * TILE, y = c.y * TILE;
        cn.moveTo(x, y).lineTo(x + Math.cos(a - f) * R, y + Math.sin(a - f) * R).arc(x, y, R, a - f, a + f).lineTo(x, y).fill({ color: 0xff3e6c, alpha: 0.06 });
      }
    });
    // Tail Job: the target's mirror cone (seen there = spotted) and its too-close ring, so the crew can judge the gap;
    // the lose range is the ring on the minimap (hud.ts) — owner request, 2026-10-12
    const tail = s.c?.find(j => j.k === 'tail' && j.parts.includes(o.you)), tv = tail && o.vehicles.find(v => v.id === tail.tv);
    if (tv) {
      const R = CHALLENGES.tail.rules!, rear = tv.a + Math.PI, f = R.spotFov / 2;
      cn.moveTo(tv.x, tv.y).lineTo(tv.x + Math.cos(rear - f) * R.spotRange, tv.y + Math.sin(rear - f) * R.spotRange).arc(tv.x, tv.y, R.spotRange, rear - f, rear + f).lineTo(tv.x, tv.y)
        .fill({ color: COLOR.warning, alpha: 0.12 }).stroke({ color: COLOR.warning, width: 1, alpha: 0.55 });
      cn.circle(tv.x, tv.y, R.closeRange).stroke({ color: COLOR.danger, width: 1.5, alpha: 0.75 });
    }
    // while you're wanted: how far each officer and police car can see you (they look all round, walls block them)
    if (o.wanted) {
      const R = CONFIG.police.detectRange;
      for (const n of o.npcs) {
        if (!POLICE_KINDS.has(n.k) || n.j || n.hp <= 0) continue;
        cn.circle(n.x, n.y, Math.min(R, NPCS[n.k].sight)).fill({ color: 0xff3e6c, alpha: 0.035 }).stroke({ color: 0xff3e6c, width: 1, alpha: 0.22 });
      }
      for (const v of o.vehicles) if (v.t === 'police' && v.o.includes(-1) && !(v.f & 16)) cn.circle(v.x, v.y, R).fill({ color: 0x3050ff, alpha: 0.03 }).stroke({ color: 0x6080ff, width: 1, alpha: 0.2 });
    }
    // fixtures hazards
    s.inf.fix.forEach((t, i) => {
      const f = this.fixG[i];
      if (t > 0) { f.tint = Math.sin(this.t * 30) > 0 ? 0xffffa0 : 0x40a0ff; if (Math.random() < 0.5) this.burst(f.x + (Math.random() - 0.5) * 60, f.y + (Math.random() - 0.5) * 60, 1, 0x80c0ff, 40, 0.2); cn.circle(f.x, f.y, CONFIG.hacks.short_circuit.hazardRadius).fill({ color: 0x60a0ff, alpha: 0.12 + 0.06 * Math.sin(this.t * 20) }); }
      else f.tint = 0xffffff;
    });
    // steam vents: drawn on the ground layer (this.infra), so steam never covers characters, pickups or markers
    const vg = this.ventG; vg.clear();
    WORLD.vents.forEach((v, i) => {
      const x = v.x * TILE, y = v.y * TILE;
      if (Math.abs(x - this.cam.x) < 400 + CONFIG.vent.radius && Math.abs(y - this.cam.y) < 300 + CONFIG.vent.radius) drawVent(vg, i, x, y, s.inf.vent[i] ?? 0, this.t, this.lowFx);
    });
    this.bossArt.draw(o.bosses ?? [], cn, this.t, this.lowFx, this.neonLevel);
    // pickups (instanced per player; only ours are sent)
    const seenPk = new Set<string>();
    for (const pk of s.pk) {
      if (pk.k === 'star') continue; // drawn with the projectiles
      seenPk.add(pk.id);
      let sp = this.pools.pk.get(pk.id);
      if (!sp) { const ik = pk.k === 'weapon' ? 'weapon' : pk.k === 'ammo' ? 'ammo' : pk.k === 'package' ? 'package' : 'cash'; sp = new Sprite(HD ? hdIconTex(ik) : art.iconTex(ik)); sp.anchor.set(0.5); if (HD) sp.scale.set(0.5); this.pickups.addChild(sp); this.pools.pk.set(pk.id, sp); }
      sp.x = pk.x; sp.y = pk.y + Math.sin(this.t * 4 + pk.x) * 1.5; sp.tint = pk.k === 'weapon' ? parseInt(pk.col.slice(1), 16) : 0xffffff;
    }
    for (const [id, sp] of this.pools.pk) if (!seenPk.has(id)) { sp.destroy(); this.pools.pk.delete(id); }
    // vehicles
    if (HD && carViewOn) warmCars(2); // bake queued headings of drawn vehicle models within a small per-frame budget
    const seenV = new Set<number>();
    for (const v of o.vehicles) {
      seenV.add(v.id);
      const wrecked = !!(v.f & 16);
      const gunner = !wrecked && !!VEHICLES[v.t].openBed?.some(i => v.o[i] > 0);
      const key = `${v.t}:${v.pc}:${wrecked}:${gunner}`;
      let e = this.pools.veh.get(v.id);
      if (!e || e.key !== key) {
        if (e) { e.s.destroy(); e.head.destroy(); e.siren.destroy(); e.sh?.destroy(); }
        const sp = new Sprite(HD ? carTex(v.t, v.pc, wrecked, gunner, decoOf(v.id, v.pc)) : art.vehicleTex(v.t, v.pc, wrecked, gunner)); sp.anchor.set(0.5);
        const head = new Sprite(this.glow); head.anchor.set(0, 0.5); head.blendMode = 'add'; head.tint = 0xfff0b0; head.alpha = 0.18;
        const siren = new Sprite(this.glow); siren.anchor.set(0.5); siren.blendMode = 'add'; siren.width = siren.height = 40;
        let sh: Sprite | undefined;
        if (HD) { sp.scale.set(0.5); sh = new Sprite(shadowTex(64, 32)); sh.anchor.set(0.5); sh.width = VEHICLES[v.t].len + 6; sh.height = VEHICLES[v.t].wid + 5; this.vehicles.addChild(sh); }
        (HD && carViewOn ? this.chars : this.vehicles).addChild(sp); this.lights.addChild(head, siren); // carview: y-sorted with people
        e = { s: sp, head, siren, key, sh }; this.pools.veh.set(v.id, e);
      }
      if (HD && carViewOn) { // review scene: pre-baked three-quarter view at the nearest of 32 headings
        const cv = drawnCarTex(v.t, v.pc, v.a, wrecked, gunner) ?? carViewTex(v.t, v.pc, wrecked, gunner, decoOf(v.id, v.pc), v.a); // drawn model if registered, else stacked
        e.s.texture = cv.tex; e.s.anchor.set(0.5, cv.anchorY); e.s.x = v.x; e.s.y = v.y; e.s.rotation = 0; e.s.zIndex = v.y;
        if (e.sh) { const ca = Math.abs(Math.cos(v.a)), sa = Math.abs(Math.sin(v.a)), vs = VEHICLES[v.t]; e.sh.x = v.x + 2; e.sh.y = v.y + 3; e.sh.rotation = 0; e.sh.width = ca * vs.len + sa * vs.wid + 6; e.sh.height = (sa * vs.len + ca * vs.wid) * TILT + 5; }
      } else {
      e.s.x = v.x; e.s.y = v.y; e.s.rotation = v.a;
      if (e.sh) { e.sh.x = v.x + 2; e.sh.y = v.y + 3; e.sh.rotation = v.a; }
      }
      e.s.tint = v.f & 2 ? (Math.sin(this.t * 20) > 0 ? 0xffe03a : 0xffffff) : 0xffffff;
      if (v.f & 32) { // EMP-stalled: the body flickers cyan and throws sparks (rare, bounded by the particle cap)
        if (Math.sin(this.t * 26 + v.id) > 0) e.s.tint = hexNum(PALETTE.cyan);
        if (Math.random() < 0.2) this.burst(v.x + (Math.random() - 0.5) * 14, v.y + (Math.random() - 0.5) * 10, 1, hexNum(PALETTE.cyan), 45, 0.25);
      }
      const L = VEHICLES[v.t].len;
      e.head.visible = !wrecked; e.head.x = v.x + Math.cos(v.a) * L / 2; e.head.y = v.y + Math.sin(v.a) * L / 2; e.head.rotation = v.a; e.head.width = 44; e.head.height = 22;
      e.siren.visible = !!(v.f & 1); if (e.siren.visible) { e.siren.x = v.x; e.siren.y = v.y; e.siren.tint = Math.floor(this.t * 6) % 2 ? 0x3050ff : 0xff2040; e.siren.alpha = 0.5; }
      if (wrecked && Math.random() < 0.3) this.burst(v.x, v.y, 1, 0xff7a2d, 20, 0.6, 2);
      if (!wrecked && vSlip(v) > SKID_SLIP && Math.hypot(v.vx, v.vy) > 40) this.skid(v.x, v.y, v.a, L, VEHICLES[v.t].wid);
    }
    for (const k of this.skids) if (k.life > 0) { k.life -= dt; k.s.alpha = 0.45 * Math.min(1, k.life / 2); if (k.life <= 0) k.s.visible = false; }
    for (const [id, e] of this.pools.veh) if (!seenV.has(id)) { e.s.destroy(); e.head.destroy(); e.siren.destroy(); e.sh?.destroy(); this.pools.veh.delete(id); }
    // characters
    const seenC = new Set<string>();
    const walkFrame = (x: number, y: number) => Math.floor((x + y) / 5) % 3;
    for (const p of o.players) {
      if (p.v || (p.fx & 4)) continue;
      const id = 'p' + p.id; seenC.add(id);
      const e = this.charSprite(id);
      const blade = p.w === 'fists' ? 'katana' : p.w === 'tanto' ? crewOf(o.players, p.id).color : null; // melee in hand: the katana, or the tanto glowing in the wielder's mask color
      const look = art.playerLook(p.ap, maskIndex(o.players, p.id));
      if (HD) this.person(e, look, { x: p.x, y: p.y, a: p.a, weapon: p.w, reload: !!p.rel, reloadSeconds: reloadSeconds(p.w, p.wr), charge: p.ch, down: !!p.d, parts: p.gp }, p.x > vx0 && p.x < vx1 && p.y > vy0 && p.y < vy1);
      else { e.s.texture = art.charTex(look, p.d ? 0 : walkFrame(p.x, p.y), !blade, blade); e.s.x = p.x; e.s.y = p.y; e.s.rotation = p.a; }
      e.s.alpha = p.d ? 0.6 : 1;
      e.s.tint = p.fx & 1 ? 0xfff080 : 0xffffff;
      e.rig?.tint(p.fx & 1 ? 0xfff080 : 0xffffff, p.d ? 0.6 : 1); if (e.rig) e.s.alpha = 1;
      this.weaponTargetFlash(e, id);
      this.wade(e, p.x, p.y, !p.d && !!this.country?.wading(p.x, p.y));
      if (this.weaponFxOn && !p.d && p.w !== 'fists' && p.w !== 'tanto' && (p.rel || p.mag === 0)) {
        const qx = Math.round(p.x + 8), qy = Math.round(p.y - (HD ? 18 : 8));
        // Reload: two magazine ticks filling down. Empty: an outlined square with a slash. Never color alone.
        if (p.rel) { cn.rect(qx, qy, 2, 5).stroke({ color: COLOR.text, width: 1, alpha: .75 }); cn.rect(qx, qy + 6, 2, 1).fill(COLOR.text); }
        else cn.rect(qx, qy, 4, 4).stroke({ color: COLOR.text, width: 1, alpha: .75 }).moveTo(qx, qy + 4).lineTo(qx + 4, qy).stroke({ color: COLOR.text, width: 1, alpha: .75 });
      }
      e.ring.visible = true; e.ring.x = p.x; e.ring.y = p.y; e.ring.tint = p.id === o.you ? 0x2de2c8 : 0x2de2c8; e.ring.alpha = p.d ? 0.2 + 0.3 * Math.sin(this.t * 8) : 0.35; e.ring.width = e.ring.height = 18;
      if (HD) { e.ring.y = p.y + 4; e.ring.height = 9; }
    }
    // Shingan (M10): read this frame's sg (wind-up, sweep, blink, decoy pops, arrival) before drawing the people
    const fxComfort = comfortScale(this.neonLevel, this.lowFx), shBody = o.s.sg ? this.pools.chr.get('n' + o.s.sg.id)?.rig?.body : undefined;
    const swung = this.shingan.update(o.s.sg, o.npcs, this.t, shBody?.texture, shBody?.scale.x ?? 1);
    this.shingan.draw(o.s.sg, o.npcs, this.t, fxComfort, this.lowFx); this.shNow = { sg: o.s.sg, npcs: o.npcs };
    this.country?.draw(this.t, dt, this.cam, o.players, fxComfort, { x0: vx0, x1: vx1, y0: vy0, y1: vy1 });
    for (const n of o.npcs) {
      if (this.shArtDraw(n, o, seenC, dt, fxComfort)) continue;
      const id = 'n' + n.id; seenC.add(id);
      const e = this.charSprite(id, n.k !== 'drone');
      // Benikage (the club boss) is an elite on the server; draw him in his haori with a katana.
      // Shingan (M10): white mask, koromo and kesa, a naginata drawn back while it winds up (sg.wu); his decoys use his
      // own look and id, so at rest they are identical (players must find the real one) apart from a brief shimmer.
      // His escorts (sg.es) are Kanemori security monks. All of them are elites on the server.
      const sg = o.s.sg, decoy = !!sg && sg.dc.includes(n.id), abbot = !!sg && (n.id === sg.id || decoy), monk = !!sg?.es?.includes(n.id);
      const oyabun = n.id === o.s.cb?.id;
      const look = abbot ? art.sanctLook('abbot', sg!.id) : monk ? art.sanctLook('monk', n.id) : oyabun ? art.npcLook('gang', n.id) : art.npcLook(n.k, n.id, n.g);
      if (HD && n.k !== 'drone') this.person(e, look, { x: n.x, y: n.y, a: n.a, charge: abbot && sg!.wu ? 1 : 0, weapon: abbot ? 'naginata' : oyabun ? 'fists' : n.g ?? (n.k === 'ped' ? '' : n.k === 'gang' ? 'smart' : n.k === 'cop' ? 'pistol' : 'v47') }, n.x > vx0 && n.x < vx1 && n.y > vy0 && n.y < vy1);
      else {
        e.s.texture = n.k === 'drone' ? (HD ? hdDroneTex : art.droneTex)(Math.floor(this.t * 12), !!n.j) : art.charTex(art.npcLook(n.k, n.id, n.g), walkFrame(n.x, n.y), n.k !== 'ped');
        e.s.x = n.x; e.s.y = n.y; e.s.rotation = n.k === 'drone' ? 0 : n.a;
        if (HD) { e.s.y = n.y - 10; e.s.zIndex = n.y + 10; e.s.scale.set(0.5); e.s.anchor.set(0.5); if (e.sh) { e.sh.visible = true; e.sh.x = n.x; e.sh.y = n.y + 2; } } // drones fly: lifted, shadow below
      }
      if (abbot && swung) e.rig?.event('melee', this.motionTime, 'naginata');
      if (decoy && e.rig) { const sh = decoyShimmer(this.t, n.id); if (sh.on && !this.lowFx) { e.rig.root.x += sh.dx; this.shingan.band(n.x, n.y - 19, 24, sh.band, fxComfort); } }
      if (e.rig && n.k === 'ped' && n.st === 0 && inClub(n.x, n.y)) e.rig.root.y -= Math.abs(Math.sin(this.t * 8 + n.id)) * 1.6; // the crowd dances (bounce on the beat)
      e.s.alpha = 1;
      e.s.tint = o.flash?.has(n.id) ? (this.weaponFxOn ? hexNum(COLOR.text) : 0xff7070) : n.k === 'drone' && n.j && Math.sin(this.t * 26 + n.id) > 0 ? hexNum(PALETTE.cyan) : 0xffffff; // hit flash; a stunned drone flickers cyan
      if (abbot && !HD && !o.flash?.has(n.id)) e.s.tint = hexNum(PALETTE.offWhite); // 1x art: Shingan and his decoys stay pale
      e.rig?.tint(e.s.tint as number);
      this.weaponTargetFlash(e, id);
      if (n.k === 'drone' && n.j && Math.random() < 0.2) this.burst(n.x + (Math.random() - 0.5) * 10, n.y + (Math.random() - 0.5) * 8, 1, hexNum(PALETTE.cyan), 40, 0.25);
      const hostile = n.st === 2 && n.k !== 'ped';
      e.ring.visible = hostile || (o.scanner && n.k !== 'ped');
      e.ring.x = n.x; e.ring.y = n.y; e.ring.tint = 0xff3e6c; e.ring.alpha = 0.3; e.ring.width = e.ring.height = 16;
      if (HD) { e.ring.y = n.y + 4; e.ring.height = 8; }
    }
    if (this.shArt && o.s.sg) { const hm = o.npcs.find(q => q.id === o.s.sg!.id); this.shArt.drawGhosts(this.t, s => this.chars.addChild(s), s => s.parent?.removeChild(s), hm ? facingSlot(hm.a) : 0); }
    this.drawDecoys(o, seenC, walkFrame);
    for (const [id, e] of this.pools.chr) if (!seenC.has(id)) { if (e.rig) e.rig.destroy(); else e.s.destroy(); e.ring.destroy(); e.sh?.destroy(); e.wm?.destroy(); this.pools.chr.delete(id); }
    // a person standing behind a lamp, pole or tree sees through it
    if (this.uprights.length) for (const u of this.uprights) {
      if (!(u.s.visible = onScreen(u.s))) continue;
      let behind = false;
      for (const p of o.players) if (!p.v && p.y < u.s.y && p.y > u.s.y - u.tall - 10 && Math.abs(p.x - u.s.x) < u.half + 6) { behind = true; break; }
      u.s.alpha = behind ? 0.45 : 1;
    }
    if (this.twin.length) { // see through a twin tower while any crew member (on foot or driving) is behind it
      const pts: { x: number; y: number }[] = [o.focus];
      for (const p of o.players) if (!p.v) pts.push(p);
      for (const v of o.vehicles) if (v.o.some(id => id > 0)) pts.push(v);
      for (const { s: ts, r: [x0, y0, x1, y1] } of this.twin) {
        const behind = pts.some(f => f.x > x0 - 6 && f.x < x1 + 6 && f.y > y0 && f.y < y1);
        ts.alpha += ((behind ? 0.32 : 1) - ts.alpha) * Math.min(1, dt * 8);
      }
    }
    // Shingan's unlit highlights, after everything that can stand in front of him has its place and alpha for this frame
    if (this.shArt) {
      const amb = this.lightOn && this.light ? this.light.ambientAt(this.cam.x, this.cam.y) : 0xffffff;
      this.shArt.endFrame([{ layer: this.chars, above: false }, { layer: this.twinLayer, above: true }], 0.9 * comfortScale(this.neonLevel, this.lowFx), nightTint(amb));
    }
    // projectiles
    const pg = this.proj; pg.clear();
    // HD: a new projectile starts drawn at the muzzle and its offset from the true path fades over 40 px of flight
    const seenP = new Set<number>();
    for (const b0 of s.pr) {
      let b = b0;
      if (HD) {
        seenP.add(b0.id);
        let st = this.projOff.get(b0.id);
        if (!st) {
          const m = this.muzzleNear(b0.x - b0.vx * 0.03, b0.y - b0.vy * 0.03);
          st = { ox: m ? m.x - (b0.x - b0.vx * 0.03) : 0, oy: m ? m.y - (b0.y - b0.vy * 0.03) : 0, x0: b0.x - b0.vx * 0.03, y0: b0.y - b0.vy * 0.03 };
          this.projOff.set(b0.id, st);
        }
        const f = Math.max(0, 1 - Math.hypot(b0.x - st.x0, b0.y - st.y0) / 40);
        if (f > 0) b = { ...b0, x: b0.x + st.ox * f, y: b0.y + st.oy * f };
      }
      if (b.k === 7) { this.drawStar(pg, b.x, b.y, this.t * 28 + b.id); continue; } // thrown ninja star: a spinning cross
      if (b.k === 8) { this.drawPellet(pg, b.x, b.y); continue; } // thrown smoke pellet
      if (b.k === 9) { this.drawKunai(pg, b.x, b.y, Math.atan2(b.vy, b.vx)); continue; } // thrown EMP kunai: a blade with an electric glow
      if (this.weaponFxOn) { this.weaponFx.projectile(b, pg, this.t); continue; }
      // 5 rifle round: long warm streak; 6 shotgun pellet: short and dim
      const col = b.k === 4 ? 0xff5050 : b.k === 2 ? 0x80ffff : b.k === 1 ? 0x60c0ff : b.k === 3 ? 0xd8ff3a : b.k === 5 ? 0xffd890 : b.k === 6 ? 0xffb860 : 0xfff0a0;
      const len = b.k === 2 ? 0.03 : b.k === 5 ? 0.022 : b.k === 6 ? 0.008 : 0.012;
      pg.moveTo(b.x, b.y).lineTo(b.x - b.vx * len, b.y - b.vy * len).stroke({ color: col, width: b.k === 2 ? 2 : 1, alpha: b.k === 6 ? 0.7 : 0.95 });
    }
    if (HD) for (const id of this.projOff.keys()) if (!seenP.has(id)) this.projOff.delete(id);
    for (const pk of s.pk) if (pk.k === 'star') this.drawStar(pg, pk.x, pk.y + (this.weaponFxOn ? 0 : Math.sin(this.t * 4 + pk.x)), Math.PI / 4, true); // a missed star waiting to be picked up
    // smoke clouds: drifting puffs (bounded: PUFFS per cloud, clouds capped by config.smoke.maxClouds)
    const sg = this.smokeG; sg.clear();
    for (const c of s.sm ?? []) {
      if (Math.abs(c.x - this.cam.x) > 400 + c.r || Math.abs(c.y - this.cam.y) > 300 + c.r) continue;
      for (const p of cloudPuffs(c, this.t, this.lowFx ? PUFFS / 2 : PUFFS)) sg.circle(p.x, p.y, p.r).fill({ color: hexNum(p.light ? PUFF_COLOR.light : PUFF_COLOR.dark), alpha: p.a });
    }
    // katana slashes: a bright arc sweeping across the swing for a moment
    this.slashes = this.slashes.filter(sl => this.t - sl.t < (sl.tanto ? TANTO_SEC : SLASH_SEC));
    for (const sl of this.slashes) {
      const sw = sl.tanto ? WEAPONS.tanto : WEAPONS.fists, reach = sw.range * 0.9, arc = sw.arc ?? 1.9, sec = sl.tanto ? TANTO_SEC : SLASH_SEC;
      const f = (this.t - sl.t) / sec, a0 = sl.a - arc / 2, fade = 1 - f;
      const sweep = (r: number, col: number, w: number, alpha: number, lag = 0) => { // an arc from the start of the swing to its current angle, its tail pulled in by `lag`
        const hi = a0 + arc * Math.min(1, f * 1.6), lo = a0 + (hi - a0) * lag;
        pg.moveTo(sl.x + Math.cos(lo) * r, sl.y + Math.sin(lo) * r).arc(sl.x, sl.y, r, lo, hi).stroke({ color: col, width: w, alpha });
      };
      if (sl.tanto) { // quick slash with a neon glow trail in the wielder's mask color: a wide soft glow, a fading tail, a bright core and a thrust spark
        sweep(reach, sl.col, 6, 0.28 * fade); sweep(reach - 1, sl.col, 3, 0.8 * fade, 0.45); sweep(reach, hexNum(PALETTE.offWhite), 1.5, fade, 0.7);
        const tip = reach + 4 * (1 - f); pg.moveTo(sl.x + Math.cos(sl.a) * 6, sl.y + Math.sin(sl.a) * 6).lineTo(sl.x + Math.cos(sl.a) * tip, sl.y + Math.sin(sl.a) * tip).stroke({ color: sl.col, width: 2, alpha: 0.7 * fade });
      } else for (const [r, col, w] of [[reach, hexNum(PALETTE.offWhite), 3], [reach - 4, hexNum(PALETTE.cyan), 2]] as const) sweep(r, col, w, fade);
    }
    // EMP bursts: a ring that swells to the burst radius and a few flickering lightning spokes (bounded: 8 bursts, 6 spokes)
    this.emps = this.emps.filter(e => this.t - e.t < EMP_SEC);
    for (const e of this.emps) {
      const f = (this.t - e.t) / EMP_SEC, R = CONFIG.emp.radius * (1 - (1 - f) ** 2), fade = 1 - f, fl = Math.floor(this.t * 30);
      pg.circle(e.x, e.y, R).stroke({ color: hexNum(PALETTE.cyan), width: 2, alpha: fade });
      pg.circle(e.x, e.y, R * 0.6).fill({ color: hexNum(PALETTE.cyan), alpha: 0.18 * fade });
      for (let i = 0; i < 6; i++) {
        let px = e.x, py = e.y; const a0 = i * Math.PI / 3 + hash2(i, fl) * 0.8;
        pg.moveTo(px, py);
        for (let s = 1; s <= 3; s++) { const a = a0 + (hash2(i + s * 7, fl) - 0.5) * 1.1, r = R * s / 3; px = e.x + Math.cos(a) * r; py = e.y + Math.sin(a) * r; pg.lineTo(px, py); }
      }
      pg.stroke({ color: hexNum(PALETTE.offWhite), width: 1, alpha: fade });
    }
    // hack targets / scanner marks
    if (this.weaponFxOn) {
      this.weaponFx.draw(pg, this.weaponGround, this.t, comfortScale(this.neonLevel, this.lowFx));
      if (!HD) for (const shot of this.weaponFx.shots.items) if (flashStrength(shot, this.t, WF.flashSec, WF.eventGraceSec) > 0) {
        muzzleFlash(pg, shot.weapon, comfortScale(this.neonLevel, this.lowFx), shot.x, shot.y, shot.a);
      }
    }
    const mk = this.marks; mk.clear();
    if (this.hackFxOn) {
      this.hackFx.draw(mk, this.hackGround, this.t, comfortScale(this.neonLevel, this.lowFx), {
        s, targets: o.targets, highlight: o.highlight, scan: o.hackMode || o.scanner, me: { x: o.focus.x, y: o.focus.y },
        vehicles: o.vehicles, npcs: o.npcs, intersections: this.intersections, cam: this.cam,
      });
    } else if (o.hackMode || o.scanner || o.highlight) for (const t of o.targets) {
      const hl = t.key === o.highlight; const r = hl ? 7 + Math.sin(this.t * 10) : 4;
      if (!hl && !o.hackMode && !o.scanner) continue;
      mk.moveTo(t.x, t.y - r).lineTo(t.x + r, t.y).lineTo(t.x, t.y + r).lineTo(t.x - r, t.y).closePath().stroke({ color: 0xd8ff3a, width: hl ? 2 : 1, alpha: hl ? 1 : 0.6 });
    }
    // particles
    for (const p of this.particles) { if (p.life <= 0) { p.s.visible = false; continue; } p.life -= dt; p.s.x += p.vx * dt; p.s.y += p.vy * dt; p.vx *= 0.92; p.vy *= 0.92; p.s.alpha = Math.max(0, p.life / p.max); }
    // rain (screen space, bounded; kept faint so it never hides hazards or aim)
    // the weather (weather.ts, shared with the rain sound): fewer, fainter streaks in a drizzle
    const wet = rainIntensity(), shown = Math.round(this.rain.length * (0.3 + 0.7 * wet));
    this.rain.forEach((r, i) => { r.s.visible = !this.lowFx && i < shown; r.s.alpha = 0.08 + 0.1 * wet; });
    if (!this.lowFx) for (const r of this.rain) { r.y += r.v * dt; r.x += r.v * 0.25 * dt; if (r.y > VH) { r.y = -10; r.x = Math.random() * VW; } if (r.x > VW) r.x -= VW; r.s.x = r.x; r.s.y = r.y; }
    this.light?.update(this.lowFx, VW, VH, s.inf.fix, o.focus);
  }
  /** two dark marks under the rear wheels */
  private skid(x: number, y: number, a: number, len: number, wid: number) {
    const max = this.lowFx ? 120 : 480, c = Math.cos(a), s = Math.sin(a), bx = x - c * len * 0.35, by = y - s * len * 0.35;
    for (const side of [-1, 1]) {
      let k = this.skids[this.skidI % max];
      if (!k) { const sp = new Sprite(this.white); sp.anchor.set(0.5); sp.tint = 0x101014; this.decals.addChild(sp); k = { s: sp, life: 0 }; this.skids[this.skidI % max] = k; }
      this.skidI++;
      k.s.x = bx - s * side * wid * 0.38; k.s.y = by + c * side * wid * 0.38; k.s.rotation = a; k.s.width = 3; k.s.height = 2;
      k.s.visible = true; k.life = 6; k.s.alpha = 0.45;
    }
  }
  shake(amp: number, x: number, y: number) {
    if (Math.hypot(x - this.cam.x, y - this.cam.y) > 260 || this.lowFx && amp < 3) return;
    this.shakeAmp = Math.max(this.shakeT > 0 ? this.shakeAmp : 0, amp); this.shakeT = 0.3;
  }
  /** Kagemusha holograms: the owner's double in their mask color, flickering, with scanlines and a sweeping band. */
  /** ?shinganart=1: Shingan and his decoys from the Art Studio sheets; false (painted abbot) until the sheets have loaded. */
  private shArtDraw(n: NpcSnap, o: { s: Snapshot; flash?: Map<number, number> }, seenC: Set<string>, dt: number, comfort: number): boolean {
    const sg = o.s.sg, A = this.shArt;
    if (!A?.ready || !sg) return false;
    const boss = n.id === sg.id;
    if (!boss && !sg.dc.includes(n.id)) return false;
    const e = this.charSprite('sa' + n.id, false);
    const pick = A.pick(n, sg, boss, this.t, dt);
    if (!A.apply(e.s, n.x, n.y, pick)) return false;
    seenC.add('sa' + n.id);
    e.s.alpha = 1; e.s.tint = o.flash?.has(n.id) ? (this.weaponFxOn ? hexNum(COLOR.text) : 0xff7070) : 0xffffff;
    if (!boss) { const sh = decoyShimmer(this.t, n.id); if (sh.on && !this.lowFx) { e.s.x += sh.dx; this.shingan.band(n.x, n.y - 19, 24, sh.band, comfort); } }
    A.glow(n.id, e.s, pick);
    if (e.sh) { e.sh.visible = true; e.sh.x = n.x; e.sh.y = n.y + 4; }
    const hostile = n.st === 2;
    e.ring.visible = hostile; e.ring.x = n.x; e.ring.y = n.y + 4; e.ring.tint = 0xff3e6c; e.ring.alpha = 0.3; e.ring.width = 16; e.ring.height = 8;
    return true;
  }
  private drawDecoys(o: { players: PlayerSnap[]; decoys?: DecoySnap[] }, seenC: Set<string>, walkFrame: (x: number, y: number) => number) {
    const g = this.decoyG; g.clear();
    for (const d of o.decoys ?? []) {
      const owner = o.players.find(p => p.id === d.id); if (!owner) continue;
      const id = 'd' + d.id; seenC.add(id);
      const e = this.charSprite(id), col = hexNum(crewOf(o.players, d.id).color), fx = decoyFx(this.t, d.id, d.m - d.l, d.l);
      const look = art.playerLook(owner.ap, maskIndex(o.players, owner.id)), fr = walkFrame(d.x, d.y);
      if (HD) this.person(e, look, { x: d.x, y: d.y, a: d.a, weapon: '' });
      else { e.s.texture = art.charTex(look, fr, false); e.s.x = d.x; e.s.y = d.y; e.s.rotation = d.a; }
      if (e.rig) { e.rig.root.x += fx.jx; e.rig.tint(col, fx.alpha); } else { e.s.x += fx.jx; e.s.tint = col; e.s.alpha = fx.alpha; }
      e.ring.visible = true; e.ring.x = d.x; e.ring.y = HD ? d.y + 4 : d.y; e.ring.tint = col; e.ring.alpha = fx.alpha * 0.5; e.ring.width = e.ring.height = 18; if (HD) e.ring.height = 9;
      // scanlines over the body box, a bright band sweeping down it, and displaced slices while it glitches
      const w = Math.max(10, e.s.width), h = Math.max(10, e.s.height), root = e.rig?.root ?? e.s, x0 = root.x - w * e.s.anchor.x, y0 = HD ? root.y - h * e.s.anchor.y : d.y - h / 2;
      for (let yy = 0; yy < h; yy += 2) g.rect(x0, y0 + yy, w, 1).fill({ color: col, alpha: 0.25 * fx.alpha });
      g.rect(x0 - 1, y0 + fx.band * h, w + 2, 2).fill({ color: hexNum(COLOR.text), alpha: 0.45 * fx.alpha });
      for (const [ry, rh, sx] of glitchBars(this.t, d.id, fx.bars)) g.rect(x0 + sx, y0 + ry * h, w, Math.max(1, rh * h)).fill({ color: col, alpha: 0.5 });
    }
  }
  private charSprite(id: string, upright = true) {
    let e = this.pools.chr.get(id);
    if (!e) {
      const ring = new Sprite(this.glow); ring.anchor.set(0.5); ring.blendMode = 'add';
      if (HD) {
        const sh = new Sprite(shadowTex(32, 12)); sh.anchor.set(0.5); sh.width = 15; sh.height = 5; sh.visible = false;
        this.under.addChild(sh, ring);
        if (upright) { const rig = new CharacterRig(this.chars); e = { s: rig.body, ring, rig, sh }; }
        else { const s = new Sprite(); s.scale.set(0.5); s.anchor.set(0.5); this.chars.addChild(s); e = { s, ring, sh }; }
      } else { const s = new Sprite(); s.anchor.set(0.5); this.chars.addChild(ring, s); e = { s, ring }; }
      this.pools.chr.set(id, e);
    }
    return e;
  }
  /** Standing in the hot spring: only the upper half shows, cut at the waterline (presentation only; the ripple is drawn by
   *  country-fx.ts). Derived from the position every client already has, so everyone sees the same. */
  private wade(e: CharacterEntry, x: number, y: number, on: boolean) {
    const target = e.rig?.root ?? e.s;
    if (!on) { if (e.wm && target.mask) { target.mask = null; e.wm.clear(); } return; }
    if (!e.wm) { e.wm = new Graphics(); this.chars.addChild(e.wm); }
    const line = y + 5 - CountryFx.WATERLINE;
    e.wm.clear().rect(x - 26, line - 70, 52, 70).fill(0xffffff);
    target.mask = e.wm;
    if (e.sh) e.sh.visible = false;
  }
  /** Foot position/hitbox remain unchanged; all pose and grip work is presentation-only. */
  private person(e: CharacterEntry, look: art.Look, input: MotionInput, visible = true) {
    e.rig!.update(look, input, this.motionTime, visible, this.lowFx, this.weaponFxOn, comfortScale(this.neonLevel, this.lowFx));
    if (e.sh) { e.sh.visible = visible; e.sh.x = input.x; e.sh.y = input.y + 4; }
    const muzzle = e.rig!.muzzle;
    e.mz = muzzle ? { bx: input.x, by: input.y, x: muzzle.x, y: muzzle.y } : undefined;
  }
  /** HD presentation only: the muzzle of the armed person whose body is nearest (x, y) and within 16 px. The server
   *  still owns the true origin and path; shots are drawn from the muzzle and blend onto the real path. */
  private muzzleNear(x: number, y: number) {
    if (!HD) return undefined;
    let best: { bx: number; by: number; x: number; y: number } | undefined, bd = 16;
    for (const e of this.pools.chr.values()) { const m = e.mz; if (!m) continue; const d = Math.hypot(m.bx - x, m.by - y); if (d < bd) { bd = d; best = m; } }
    return best;
  }
  /** A ninja star: a four-point cross turned by `rot` (flying) or resting (dimmer). */
  private drawStar(pg: Graphics, x: number, y: number, rot: number, resting = false) {
    if (this.weaponFxOn) { x = Math.round(x); y = Math.round(y); rot = Math.round(rot / (Math.PI / 4)) * Math.PI / 4; }
    const col = hexNum(resting ? PALETTE.cyan : PALETTE.offWhite), r = 3.2;
    for (let i = 0; i < 4; i++) { const a = rot + i * Math.PI / 2; pg.moveTo(x, y).lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r); }
    pg.stroke({ color: col, width: 1.5, alpha: resting ? 0.85 : 1 });
    if (this.weaponFxOn && (!resting || Math.floor(this.t * 2 + x) % 4 === 0)) {
      const scale = comfortScale(this.neonLevel, this.lowFx);
      pg.moveTo(x - 4, y).lineTo(x + 4, y).moveTo(x, y - 4).lineTo(x, y + 4).stroke({ color: COLOR.text, width: 1, alpha: .55 * scale });
    }
  }
  /** An EMP kunai in flight: a short blade pointing along `a` with a cyan glow and a crackling tail. */
  private drawKunai(pg: Graphics, x: number, y: number, a: number) {
    if (this.weaponFxOn) { x = Math.round(x); y = Math.round(y); }
    const c = Math.cos(a), s = Math.sin(a), cy = hexNum(PALETTE.cyan);
    pg.circle(x, y, 5).fill({ color: cy, alpha: .22 * (this.weaponFxOn ? comfortScale(this.neonLevel, this.lowFx) : 1) });
    pg.moveTo(x - c * 5, y - s * 5).lineTo(x + c * 5, y + s * 5).stroke({ color: hexNum(PALETTE.offWhite), width: 2 });
    pg.moveTo(x + c * 5, y + s * 5).lineTo(x + c * 7, y + s * 7).stroke({ color: cy, width: 1.5 });
    const j = Math.sin(this.t * 90 + x) * 2;
    pg.moveTo(x - c * 5, y - s * 5).lineTo(x - c * 9 - s * j, y - s * 9 + c * j).lineTo(x - c * 13 + s * j, y - s * 13 - c * j).stroke({ color: cy, width: 1, alpha: 0.9 });
  }
  /** A smoke pellet in flight: a dark ball with a bright rim and a lit fuse. */
  private drawPellet(pg: Graphics, x: number, y: number) {
    if (this.weaponFxOn) { x = Math.round(x); y = Math.round(y); }
    pg.circle(x, y, 2.6).fill({ color: hexNum(PALETTE.midnight) }).stroke({ color: hexNum(PALETTE.offWhite), width: 1 });
    pg.circle(x + 2, y - 3, 1).fill({ color: hexNum(PALETTE.amber), alpha: this.weaponFxOn ? .7 * comfortScale(this.neonLevel, this.lowFx) : 0.6 + 0.4 * Math.sin(this.t * 40) });
  }
  /** `col`: the swinging player's crew mask color (a tanto's glow). */
  event(k: string, x: number, y: number, w?: string, a?: number, col?: string, by?: number, source?: PlayerSnap) {
    if(k==='shot'&&by===0)this.bossArt.shot(x,y,this.t);
    // Player events carry their owner. NPC gun events use an exact nearby body; vehicle/boss shots never kick a runner.
    let actor = by !== undefined && by > 0 ? this.pools.chr.get('p' + by) : undefined;
    if (by === undefined && (w === 'npc' || w === 'vs12' || w === 'k5' || w === 'smart')) { // NPC gun events carry the faction gun id
      let best = 4;
      for (const [id, e] of this.pools.chr) if (id.startsWith('n') && e.rig) {
        const d = Math.hypot(e.rig.motion.x - x, e.rig.motion.y - y);
        if (d < best) { actor = e; best = d; }
      }
    }
    if (k === 'melee' && w === 'naginata') return; // Shingan's wind-up and sweep are drawn from the snapshot (shingan-fx.ts)
    if (k === 'boom' && (w === 'elec' || w === 'shockwave') && this.shingan.isHim(this.shNow.sg, this.shNow.npcs, x, y)) { this.shingan.shock(x, y, this.t); this.shArt?.boom(this.t); }
    if (k !== 'melee' || w) actor?.rig?.event(k, this.motionTime, w); // an unarmed tackle event has no blade
    if (!(this.weaponFxOn && ((k === 'shot' && by !== 0) || k === 'hit' || k === 'spark')) && !(this.hackFxOn && k === 'hack')) this.light?.flash(k, x, y);
    if (this.hackFxOn && k === 'hack') return; // drawn by hackConfirm() from the same authoritative event, with its target and duration
    if (k === 'smoke') { this.burst(x, y, this.lowFx ? 6 : 14, hexNum(PALETTE.offWhite), 70, 0.7, 2); this.burst(x, y, this.lowFx ? 3 : 8, hexNum(PALETTE.slate), 45, 0.9, 2); return; }
    if (k === 'emp') { this.emps.push({ x, y, t: this.t }); if (this.emps.length > 8) this.emps.shift(); this.burst(x, y, 10, hexNum(PALETTE.cyan), 90, 0.4); this.burst(x, y, 6, hexNum(PALETTE.offWhite), 60, 0.25); if (!this.weaponFxOn) this.shake(2, x, y); }
    if (k === 'melee' && a !== undefined && (!HD || !actor?.rig)) this.slashes.push({ x: x - Math.cos(a) * 10, y: y - Math.sin(a) * 10, a, t: this.t, tanto: w === 'tanto', col: hexNum(col ?? PALETTE.cyan) });
    if (this.weaponFxOn && this.slashes.length > WF.decals) this.slashes.shift();
    if (this.weaponFxOn && k === 'shot' && by !== 0) {
      const p = source ?? this.weaponPlayers.find(p => p.id === by), m = actor?.mz ?? this.muzzleNear(x, y);
      const angle = actor?.rig?.motion.aim ?? p?.a ?? a ?? 0;
      this.weaponFx.shot(m?.x ?? x, m?.y ?? y, angle, w ?? p?.w ?? 'pistol', p?.wr ?? 'common', this.t, p?.x ?? x, p?.y ?? y);
      this.light?.weaponFlash(m?.x ?? x, m?.y ?? y, comfortScale(this.neonLevel, this.lowFx)); return;
    }
    if (this.weaponFxOn && (k === 'hit' || k === 'spark')) { this.weaponFx.impact(x, y, k === 'hit', this.t); return; }
    if (k === 'shot') { const m = this.muzzleNear(x, y); this.burst(m ? m.x : x, m ? m.y : y, 2, 0xfff0a0, 60, 0.12); }
    else if (k === 'hit') this.burst(x, y, 4, 0xff4060, 60, 0.3);
    else if (k === 'spark') this.burst(x, y, 3, 0xffe080, 80, 0.2);
    else if (k === 'boom') { this.shake(5, x, y); this.burst(x, y, 30, 0xff7a2d, 120, 0.8, 2); this.burst(x, y, 12, 0xffe03a, 80, 0.5, 2); if (w === 'elec' || w === 'shockwave') this.burst(x, y, 10, 0x60c0ff, 100, 0.5); }
    else if (k === 'crash') { this.burst(x, y, 6, 0xc0c0d0, 70, 0.3); this.shake(3, x, y); }
    else if (k === 'hack') { this.burst(x, y, 14, 0xd8ff3a, 50, 0.6); this.burst(x, y, 8, 0x2de2c8, 30, 0.8); }
    else if (k === 'melee') this.burst(x, y, 3, 0xffffff, 40, 0.15);
    else if (k === 'vault') this.burst(x, y, 6, 0x2de2c8, 40, 0.3);
    else if (k === 'pickup') this.burst(x, y, 6, 0xffe03a, 40, 0.3);
    else if (k === 'decoy') this.burst(x, y, w === 'on' ? 10 : 14, hexNum(COLOR.interactive), 50, 0.4); // pixels of light as the double appears / dissolves
  }
  /** Local key press: the acknowledgement link only. Nothing here claims the server accepted it. */
  hackAck(from: { x: number; y: number }, h: HackId, target: HackTarget, predictedOk: boolean) {
    if (this.hackFxOn) this.hackFx.ack(from, target, h, target.key, this.t, predictedOk);
  }
  /** An accepted hack (any player's), from the server event and the snapshot it arrived in. */
  hackConfirm(e: GameEvent, sn: Snapshot) {
    if (!this.hackFxOn || !e.tk) return;
    const hacker = sn.p.find(p => p.id === e.by), i = Number(e.tk.split(':')[1]), veh = e.tk.startsWith('veh:') ? sn.v.find(v => v.id === i) : undefined;
    this.hackFx.confirm(e, hacker, veh?.a, e.tk.startsWith('bar:') ? !!sn.inf.bar[i] : undefined, this.t);
    this.light?.hackFlash(e.x, e.y, e.w ?? '', comfortScale(this.neonLevel, this.lowFx));
  }
  /** A refused hack's reason code from the server: drawn as a refusal glitch when it answers the last hack request. */
  hackRefuse(code: RefusalCode | undefined): string | null { return this.hackFxOn ? this.hackFx.refuse(code, this.t) : null; }
  /** Exact target identity and damage come from the existing silent authoritative dmg event. */
  weaponDamage(e: GameEvent, players: PlayerSnap[]) {
    if (!this.weaponFxOn || e.k !== 'dmg') return;
    const by = players.find(p => p.id === e.by), key = e.n !== undefined ? 'n' + e.n : e.to !== undefined ? 'p' + e.to : '';
    if (!key) return; // boss feedback is owned by the boss branch
    this.weaponFx.damage(key, e.d ?? 0, e.x, e.y - (HD ? 10 : 0), by?.a ?? 0, by?.w === 'fists' || by?.w === 'tanto', this.t);
  }
  private weaponTargetFlash(e: CharacterEntry, key: string) {
    const root = e.rig?.root ?? e.s, hit = this.weaponFxOn && this.weaponFx.hit(key, this.t);
    if (hit) {
      if (!this.weaponHitFilter) { this.weaponHitFilter = new ColorMatrixFilter(); this.weaponHitFilter.resolution = 'inherit'; }
      this.weaponHitFilter.brightness(1 + .35 * comfortScale(this.neonLevel, this.lowFx), false);
      if (!root.filters?.includes(this.weaponHitFilter)) root.filters = [...(root.filters ?? []), this.weaponHitFilter];
    } else if (this.weaponHitFilter && root.filters?.includes(this.weaponHitFilter)) root.filters = root.filters.filter(f => f !== this.weaponHitFilter);
  }
}
